import React, { useState, useEffect } from 'react';
import { View, StyleSheet, ScrollView, Alert, TouchableOpacity } from 'react-native';
import { Text, Button, Card, TextInput, Divider, ActivityIndicator, IconButton, Menu } from 'react-native-paper';
import { useNavigation } from '@react-navigation/native';
import * as Print from 'expo-print';
import * as Sharing from 'expo-sharing';
import * as FileSystem from 'expo-file-system/legacy';
import AsyncStorage from '@react-native-async-storage/async-storage';
import { Ionicons } from '@expo/vector-icons';
import api from '../../services/api';
import { generateCotizacionHTML } from '../../utils/facturaGenerator';

const NuevaCotizacionScreen = () => {
  const navigation = useNavigation();

  const [loading, setLoading] = useState(false);
  const [clientes, setClientes] = useState([]);
  const [productos, setProductos] = useState([]);
  const [clienteSearch, setClienteSearch] = useState('');
  const [productoSearch, setProductoSearch] = useState('');
  const [showClientesList, setShowClientesList] = useState(false);
  const [showProductosList, setShowProductosList] = useState(false);
  const [cotizacion, setCotizacion] = useState({
    cliente: null,
    items: [],
    total: 0,
    notas: '',
    dias_vigencia: '15',
  });
  const [presentacionMenuIndex, setPresentacionMenuIndex] = useState(null);

  useEffect(() => {
    const loadData = async () => {
      try {
        setLoading(true);
        const [clientesData, productosData] = await Promise.all([
          api.getClientes(),
          api.getProductos(),
        ]);
        setClientes(clientesData);
        setProductos(productosData);
      } catch (error) {
        console.error('Error al cargar datos:', error);
        Alert.alert('Error', 'No se pudieron cargar los datos necesarios');
      } finally {
        setLoading(false);
      }
    };
    loadData();
  }, []);

  const filteredClientes = clienteSearch === ''
    ? clientes.slice(0, 10)
    : clientes.filter(c =>
        (c.nombre || '').toLowerCase().includes(clienteSearch.toLowerCase()) ||
        (c.telefono || '').includes(clienteSearch) ||
        (c.direccion || '').toLowerCase().includes(clienteSearch.toLowerCase())
      ).slice(0, 20);

  const filteredProductos = productoSearch === ''
    ? productos
    : productos.filter(p =>
        (p.nombre || '').toLowerCase().includes(productoSearch.toLowerCase()) ||
        String(p.id || '').includes(productoSearch)
      );

  const handleSelectCliente = (cliente) => {
    setCotizacion({ ...cotizacion, cliente });
    setShowClientesList(false);
    setClienteSearch('');
  };

  const handleAddProducto = (producto) => {
    setShowProductosList(false);
    setProductoSearch(producto.nombre);

    const precio = parseFloat(producto.precio_venta) || parseFloat(producto.precio) || 0;
    if (!precio || precio <= 0) {
      Alert.alert('Error', 'Este producto no tiene precio configurado.');
      return;
    }

    const existingIdx = cotizacion.items.findIndex(item => item.producto.id === producto.id);
    if (existingIdx !== -1) {
      const updatedItems = [...cotizacion.items];
      updatedItems[existingIdx].cantidad += 1;
      updatedItems[existingIdx].subtotal = Math.round(updatedItems[existingIdx].precio_unitario * updatedItems[existingIdx].cantidad * 100) / 100;
      updateItems(updatedItems);
    } else {
      updateItems([...cotizacion.items, {
        producto,
        cantidad: 1,
        precio_unitario: precio,
        subtotal: Math.round(precio * 100) / 100,
        presentacion_id: null,
        factor_conversion: 1,
        unidad_nombre: 'Unidad',
      }]);
    }
    setTimeout(() => setProductoSearch(''), 1000);
  };

  const handleRemoveProducto = (index) => {
    const updated = [...cotizacion.items];
    updated.splice(index, 1);
    updateItems(updated);
  };

  const handleUpdateCantidad = (index, cantidad) => {
    if (cantidad <= 0) return;
    const updated = [...cotizacion.items];
    updated[index].cantidad = cantidad;
    updated[index].subtotal = Math.round(updated[index].precio_unitario * cantidad * 100) / 100;
    updateItems(updated);
  };

  const handleChangePresentacion = (index, pp) => {
    const updated = [...cotizacion.items];
    const item = updated[index];
    const precioBase = parseFloat(item.producto?.precio_venta) || parseFloat(item.producto?.precio) || 0;
    const factor = pp ? Number(pp.factor) : 1;
    const nombre = pp ? pp.nombre : 'Unidad';
    const presId = pp ? pp.presentacion_id : null;
    const nuevoPrecio = Math.round(precioBase * factor * 100) / 100;
    updated[index] = {
      ...item,
      cantidad: 1,
      precio_unitario: nuevoPrecio,
      subtotal: nuevoPrecio,
      factor_conversion: factor,
      unidad_nombre: nombre,
      presentacion_id: presId,
    };
    updateItems(updated);
    setPresentacionMenuIndex(null);
  };

  const updateItems = (items) => {
    const total = Math.round(items.reduce((sum, item) => sum + item.subtotal, 0) * 100) / 100;
    setCotizacion({ ...cotizacion, items, total });
  };

  const formatCurrency = (value) => `L. ${parseFloat(value || 0).toFixed(2)}`;

  const handleCompartirPDF = async (data) => {
    try {
      let config = {};
      try {
        config = await api.getConfiguracion();
      } catch (e) {
        console.log('[PDF] Error al cargar config:', e.message);
      }

      if (!config.logo_url) {
        try {
          const logoPath = FileSystem.documentDirectory + 'logo.png';
          const logoExists = await FileSystem.getInfoAsync(logoPath);
          if (logoExists.exists) {
            const base64 = await FileSystem.readAsStringAsync(logoPath, {
              encoding: FileSystem.EncodingType.Base64,
            });
            config.logo_url = `data:image/png;base64,${base64}`;
          }
        } catch (logoError) {
          console.log('No se pudo cargar logo local:', logoError);
        }
      }

      const html = generateCotizacionHTML(data, config);
      const { uri } = await Print.printToFileAsync({ html, base64: false });
      if (await Sharing.isAvailableAsync()) {
        await Sharing.shareAsync(uri);
      }
    } catch (error) {
      Alert.alert('Error', 'No se pudo generar el PDF');
    }
  };

  const handleGuardar = async () => {
    if (!cotizacion.cliente) {
      Alert.alert('Error', 'Debe seleccionar un cliente');
      return;
    }
    if (cotizacion.items.length === 0) {
      Alert.alert('Error', 'Debe agregar al menos un producto');
      return;
    }

    try {
      setLoading(true);
      const userStr = await AsyncStorage.getItem('user');
      const user = userStr ? JSON.parse(userStr) : null;
      if (!user || !user.id) {
        Alert.alert('Error', 'Sesion expirada. Inicie sesion nuevamente.');
        return;
      }

      const dias = parseInt(cotizacion.dias_vigencia) || 15;
      const fechaVencimiento = new Date(Date.now() + dias * 24 * 60 * 60 * 1000).toISOString().slice(0, 10);

      const data = {
        cliente_id: cotizacion.cliente.id,
        items: cotizacion.items.map(item => {
          const factor = Number(item.factor_conversion) > 0 ? Number(item.factor_conversion) : 1;
          return {
            producto_id: item.producto.id,
            cantidad: item.cantidad * factor,
            precio_unitario: Math.round((item.precio_unitario / factor) * 100) / 100,
            presentacion_id: item.presentacion_id || null,
            factor_conversion: factor,
            unidad_nombre: item.unidad_nombre || 'Unidad',
          };
        }),
        notas: cotizacion.notas,
        fecha_vencimiento: fechaVencimiento,
      };

      const response = await api.createCotizacion(data);

      if (response && response.id) {
        Alert.alert(
          'Cotizacion Creada',
          `${response.numero}\nTotal: ${formatCurrency(response.total)}\nVigente hasta: ${response.fecha_vencimiento}`,
          [
            { text: 'Cerrar', onPress: () => navigation.goBack() },
            {
              text: 'Compartir PDF',
              onPress: () => {
                handleCompartirPDF({
                  numero: response.numero,
                  fecha: new Date().toLocaleDateString('es-HN'),
                  cliente: cotizacion.cliente,
                  items: cotizacion.items,
                  total: cotizacion.total,
                  notas: cotizacion.notas,
                  dias_vigencia: dias,
                  vendedor: user.nombre || user.email,
                });
                navigation.goBack();
              }
            },
          ]
        );
      }
    } catch (error) {
      Alert.alert('Error', error.message);
    } finally {
      setLoading(false);
    }
  };

  if (loading && clientes.length === 0) {
    return (
      <View style={styles.loadingContainer}>
        <ActivityIndicator size="large" color="#007BFF" />
        <Text style={styles.loadingText}>Cargando...</Text>
      </View>
    );
  }

  return (
    <ScrollView style={styles.container}>
      {/* Seleccion de cliente */}
      <Card style={styles.card}>
        <Card.Title title="Cliente" />
        <Card.Content>
          {cotizacion.cliente ? (
            <View style={styles.clienteSeleccionado}>
              <View style={{ flex: 1 }}>
                <Text style={styles.clienteNombre}>{cotizacion.cliente.nombre}</Text>
                <Text style={styles.clienteDetalle}>{cotizacion.cliente.telefono || ''}</Text>
              </View>
              <IconButton icon="close" size={20} onPress={() => setCotizacion({ ...cotizacion, cliente: null })} />
            </View>
          ) : (
            <View>
              <TextInput
                label="Buscar cliente"
                value={clienteSearch}
                onChangeText={(text) => { setClienteSearch(text); setShowClientesList(true); }}
                onFocus={() => setShowClientesList(true)}
                right={<TextInput.Icon icon={showClientesList ? "close" : "magnify"} onPress={() => { setShowClientesList(!showClientesList); setClienteSearch(''); }} />}
                style={styles.searchInput}
              />
              {showClientesList && (
                <Card style={styles.dropdown}>
                  <ScrollView style={{ maxHeight: 200 }} nestedScrollEnabled>
                    {filteredClientes.map(c => (
                      <TouchableOpacity key={c.id} onPress={() => handleSelectCliente(c)} style={styles.dropdownItem}>
                        <Text style={styles.dropdownItemName}>{c.nombre}</Text>
                        {c.telefono && <Text style={styles.dropdownItemDetail}>{c.telefono}</Text>}
                      </TouchableOpacity>
                    ))}
                    {filteredClientes.length === 0 && (
                      <Text style={styles.emptyText}>No se encontraron clientes</Text>
                    )}
                  </ScrollView>
                </Card>
              )}
            </View>
          )}
        </Card.Content>
      </Card>

      {/* Productos */}
      <Card style={styles.card}>
        <Card.Title title="Productos" />
        <Card.Content>
          <TextInput
            label="Buscar producto"
            value={productoSearch}
            onChangeText={(text) => { setProductoSearch(text); setShowProductosList(true); }}
            onFocus={() => setShowProductosList(true)}
            right={<TextInput.Icon icon={productoSearch ? "close" : "magnify"} onPress={() => { setProductoSearch(''); setShowProductosList(false); }} />}
            style={styles.searchInput}
          />
          {showProductosList && (
            <Card style={styles.dropdown}>
              <ScrollView style={{ maxHeight: 250 }} nestedScrollEnabled>
                {filteredProductos.slice(0, 10).map(p => (
                  <TouchableOpacity key={p.id} onPress={() => handleAddProducto(p)} style={styles.productItem}>
                    <View style={{ flex: 1 }}>
                      <Text style={styles.productName}>{p.nombre}</Text>
                      <View style={{ flexDirection: 'row' }}>
                        <Text style={styles.productPrice}>{formatCurrency(p.precio_venta || p.precio || 0)}</Text>
                        <Text style={styles.productStock}> Stock: {p.stock_total || p.stock || 0}</Text>
                      </View>
                    </View>
                    <View style={styles.addBtn}><Text style={styles.addBtnText}>+</Text></View>
                  </TouchableOpacity>
                ))}
                {filteredProductos.length === 0 && <Text style={styles.emptyText}>Sin resultados</Text>}
              </ScrollView>
            </Card>
          )}

          <Divider style={{ marginVertical: 10 }} />

          {cotizacion.items.length > 0 ? (
            cotizacion.items.map((item, index) => (
              <View key={index} style={styles.itemRow}>
                <View style={{ flex: 1 }}>
                  <Text style={styles.itemName}>{item.producto.nombre}</Text>
                  <Text style={styles.itemDetail}>
                    {formatCurrency(item.precio_unitario)} x {item.cantidad}
                    {Number(item.factor_conversion) > 1 ? ` ${item.unidad_nombre}` : ''}
                    {' = '}{formatCurrency(item.subtotal)}
                  </Text>
                  {Array.isArray(item.producto.presentaciones) && item.producto.presentaciones.length > 0 && (
                    <View style={styles.presentacionRow}>
                      <Text style={styles.presentacionLabel}>Pres:</Text>
                      <Menu
                        visible={presentacionMenuIndex === index}
                        onDismiss={() => setPresentacionMenuIndex(null)}
                        anchor={
                          <TouchableOpacity
                            style={styles.presentacionChip}
                            onPress={() => setPresentacionMenuIndex(index)}
                          >
                            <Text style={styles.presentacionChipText}>{item.unidad_nombre || 'Unidad'} ▾</Text>
                          </TouchableOpacity>
                        }
                      >
                        <Menu.Item title="Unidad (x1)" onPress={() => handleChangePresentacion(index, null)} />
                        {item.producto.presentaciones.map((pp) => (
                          <Menu.Item
                            key={pp.id}
                            title={`${pp.nombre} (x${Number(pp.factor_conversion)})`}
                            onPress={() => handleChangePresentacion(index, {
                              presentacion_id: pp.presentacion_id,
                              nombre: pp.nombre,
                              factor: Number(pp.factor_conversion),
                            })}
                          />
                        ))}
                      </Menu>
                    </View>
                  )}
                </View>
                <View style={styles.itemActions}>
                  <IconButton icon="minus" size={18} onPress={() => handleUpdateCantidad(index, item.cantidad - 1)} disabled={item.cantidad <= 1} />
                  <Text style={styles.cantidadText}>{item.cantidad}</Text>
                  <IconButton icon="plus" size={18} onPress={() => handleUpdateCantidad(index, item.cantidad + 1)} />
                  <IconButton icon="delete" size={18} iconColor="#DC3545" onPress={() => handleRemoveProducto(index)} />
                </View>
              </View>
            ))
          ) : (
            <Text style={styles.emptyText}>No hay productos agregados</Text>
          )}
        </Card.Content>
      </Card>

      {/* Vigencia y notas */}
      <Card style={styles.card}>
        <Card.Title title="Detalles" />
        <Card.Content>
          <TextInput
            label="Dias de vigencia"
            value={cotizacion.dias_vigencia}
            onChangeText={(text) => setCotizacion({ ...cotizacion, dias_vigencia: text.replace(/[^0-9]/g, '') })}
            keyboardType="numeric"
            style={styles.searchInput}
            right={<TextInput.Affix text="dias" />}
          />
          <TextInput
            label="Notas (opcional)"
            value={cotizacion.notas}
            onChangeText={(text) => setCotizacion({ ...cotizacion, notas: text })}
            multiline
            numberOfLines={2}
            style={[styles.searchInput, { marginTop: 8 }]}
          />
        </Card.Content>
      </Card>

      {/* Total */}
      <Card style={styles.card}>
        <Card.Content style={styles.totalContainer}>
          <Text style={styles.totalLabel}>TOTAL COTIZACION:</Text>
          <Text style={styles.totalValue}>{formatCurrency(cotizacion.total)}</Text>
        </Card.Content>
      </Card>

      {/* Botones */}
      <View style={styles.buttonsContainer}>
        <Button mode="outlined" onPress={() => navigation.goBack()} style={styles.btn}>
          Cancelar
        </Button>
        <Button
          mode="contained"
          onPress={handleGuardar}
          style={[styles.btn, { backgroundColor: '#007BFF' }]}
          disabled={loading || !cotizacion.cliente || cotizacion.items.length === 0}
          loading={loading}
          icon="content-save"
        >
          Guardar Cotizacion
        </Button>
      </View>

      <View style={{ height: 30 }} />
    </ScrollView>
  );
};

const styles = StyleSheet.create({
  container: { flex: 1, backgroundColor: '#f5f5f5', padding: 8 },
  loadingContainer: { flex: 1, justifyContent: 'center', alignItems: 'center' },
  loadingText: { marginTop: 10, color: '#666' },
  card: { marginBottom: 8, elevation: 2 },
  searchInput: { backgroundColor: '#fff', marginBottom: 4 },
  // Cliente
  clienteSeleccionado: { flexDirection: 'row', alignItems: 'center', backgroundColor: '#E8F5E9', padding: 12, borderRadius: 8, borderWidth: 1, borderColor: '#28A745' },
  clienteNombre: { fontSize: 16, fontWeight: 'bold', color: '#28A745' },
  clienteDetalle: { fontSize: 13, color: '#666', marginTop: 2 },
  // Dropdown
  dropdown: { maxHeight: 250, elevation: 4, marginTop: 4 },
  dropdownItem: { padding: 12, borderBottomWidth: 1, borderBottomColor: '#f0f0f0' },
  dropdownItemName: { fontSize: 14, fontWeight: '600', color: '#333' },
  dropdownItemDetail: { fontSize: 12, color: '#888', marginTop: 2 },
  // Products
  productItem: { flexDirection: 'row', alignItems: 'center', padding: 10, borderBottomWidth: 1, borderBottomColor: '#f0f0f0' },
  productName: { fontSize: 14, fontWeight: '600', color: '#333' },
  productPrice: { fontSize: 12, color: '#28A745', marginTop: 2 },
  productStock: { fontSize: 12, color: '#888', marginTop: 2 },
  addBtn: { width: 30, height: 30, borderRadius: 15, backgroundColor: '#007BFF', alignItems: 'center', justifyContent: 'center' },
  addBtnText: { color: '#fff', fontSize: 18, fontWeight: 'bold' },
  // Items
  itemRow: { flexDirection: 'row', alignItems: 'center', paddingVertical: 8, borderBottomWidth: 1, borderBottomColor: '#f0f0f0' },
  itemName: { fontSize: 14, fontWeight: '600', color: '#333' },
  itemDetail: { fontSize: 12, color: '#666', marginTop: 2 },
  itemActions: { flexDirection: 'row', alignItems: 'center' },
  cantidadText: { fontSize: 16, fontWeight: 'bold', minWidth: 24, textAlign: 'center' },
  emptyText: { textAlign: 'center', color: '#888', padding: 16 },
  presentacionRow: { flexDirection: 'row', alignItems: 'center', marginTop: 4 },
  presentacionLabel: { fontSize: 12, color: '#666', marginRight: 6 },
  presentacionChip: { borderWidth: 1, borderColor: '#0066cc', paddingHorizontal: 10, paddingVertical: 3, borderRadius: 12, backgroundColor: '#E7F1FF' },
  presentacionChipText: { fontSize: 12, color: '#0066cc', fontWeight: '600' },
  // Modal
  modalContainer: { padding: 20 },
  modalCard: { maxHeight: '80%' },
  modalSubtitle: { fontSize: 14, color: '#666', marginBottom: 8 },
  unidadItem: { paddingVertical: 4 },
  unidadRow: { flexDirection: 'row', alignItems: 'center' },
  unidadInfo: { flex: 1, marginLeft: 8 },
  unidadNombre: { fontSize: 15, fontWeight: 'bold' },
  // Total
  totalContainer: { flexDirection: 'row', justifyContent: 'space-between', alignItems: 'center' },
  totalLabel: { fontSize: 16, fontWeight: 'bold', color: '#333' },
  totalValue: { fontSize: 22, fontWeight: 'bold', color: '#28A745' },
  // Buttons
  buttonsContainer: { flexDirection: 'row', justifyContent: 'space-between', marginVertical: 8 },
  btn: { flex: 1, marginHorizontal: 4 },
});

export default NuevaCotizacionScreen;
